import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../core/auth';
import { supabase } from '../core/supabase';
import { useAvisar } from '../ui/aviso';

/**
 * Camada de dados sobre o Supabase.
 *
 * Regras que valem para tudo aqui (documentos 04, 05 e 18):
 * - o `org_id` NUNCA é enviado pelo cliente; um trigger no banco o preenche a
 *   partir do JWT, e a RLS recusa qualquer linha fora do tenant;
 * - toda chave de cache carrega o `orgId`, então trocar de organização não
 *   reaproveita dado de outra;
 * - erro de escrita sobe para a tela, nunca é engolido.
 */

export type Table =
  | 'profiles'
  | 'organizations'
  | 'memberships'
  | 'app_installations'
  | 'entitlements'
  | 'flag_overrides'
  | 'audit_logs'
  | 'api_keys'
  | 'webhooks'
  | 'contacts'
  | 'segments'
  | 'landing_pages'
  | 'forms'
  | 'popups'
  | 'email_campaigns'
  | 'email_senders'
  | 'email_layouts'
  | 'automations'
  | 'automation_queue'
  | 'notifications'
  | 'tracked_domains'
  | 'tracking_events'
  | 'scoring_rules'
  | 'teams'
  | 'pipelines'
  | 'stages'
  | 'deals'
  | 'tasks'
  | 'activities'
  | 'deal_stage_history'
  | 'custom_fields'
  // CRM · empresas e configuração da equipe (0109)
  | 'companies'
  | 'crm_settings'
  | 'sales_automations'
  | 'forecasts'
  | 'dashboards'
  | 'channels'
  | 'message_templates'
  | 'queues'
  | 'conversations'
  | 'messages'
  | 'chatbots'
  | 'chatbot_flows'
  | 'lead_qualificacoes'
  | 'ai_agents'
  | 'quick_replies'
  | 'flw_materiais_do_robo'
  | 'cima_unidades_preco'
  | 'cima_unidades_historico'
  | 'cima_interesses_unidades'
  | 'campaigns'
  | 'feature_flags'
  // POP · Flow Pop-up
  | 'pop_widgets'
  | 'pop_respostas'
  | 'pop_atendentes'
  | 'pop_passos'
  | 'pop_desempenho'
  // Rastro
  | 'rastro_visitantes'
  | 'rastro_eventos'
  // BIO
  | 'platform_admins'
  | 'yard_clientes'
  | 'yard_usuarios'
  | 'yard_convites'
  | 'yard_auditoria'
  | 'yard_recursos'
  | 'bio_entitlements'
  | 'bio_sites'
  | 'bio_surfaces'
  | 'bio_versions'
  | 'bio_blocks'
  | 'bio_links'
  | 'bio_themes'
  | 'bio_products'
  | 'bio_collections'
  | 'bio_coupons'
  | 'bio_customers'
  | 'bio_orders'
  | 'bio_order_items'
  | 'bio_payments'
  | 'bio_quizzes'
  | 'bio_quiz_questions'
  | 'bio_quiz_options'
  | 'bio_quiz_outcomes'
  | 'bio_quiz_campos'
  | 'bio_quiz_sessions'
  | 'bio_services'
  | 'bio_appointments'
  | 'bio_reviews'
  | 'bio_imoveis'
  | 'bio_imovel_units'
  | 'bio_events'
  // MAIL · Portal
  | 'portal_contas'
  | 'portal_contas_visiveis'
  | 'portal_mensagens'
  | 'portal_anexos'
  | 'portal_documentos'
  | 'portal_documento_acessos'
  /* Da 0068. Enquanto ela não roda, quem consulta estas três recebe PGRST205 e
     a tela degrada em vez de quebrar - ver `aguardandoMigracao` abaixo. */
  | 'portal_pastas'
  | 'portal_documento_compartilhamentos'
  | 'portal_documento_pessoas'
  | 'portal_tipos_reuniao'
  | 'portal_disponibilidade'
  | 'portal_bloqueios'
  | 'portal_reunioes'
  | 'portal_config'
  // MAIL · reunião: ata, transcrição e gravação (0069) e delegações (0070)
  | 'reuniao_transcricao'
  | 'reuniao_ata'
  | 'reuniao_gravacao'
  | 'portal_delegacoes'
  // MKT · biblioteca de e-mails (0067)
  | 'email_templates'
  | 'email_gatilhos'
  | 'email_testes'
  | 'email_disparos'
  | 'email_assinaturas'
  | 'email_pessoas_da_equipe';

export type Row = Record<string, unknown> & { id: string };

type ListOptions = {
  select?: string;
  orderBy?: string;
  ascending?: boolean;
  eq?: Record<string, string | number | boolean | null>;
  /** `coluna >= valor`. Serve ao recorte de período feito no servidor. */
  gte?: Record<string, string | number>;
  /**
   * Teto de linhas. O PostgREST já corta em 1000 sem avisar; declarar o teto
   * aqui deixa explícito que a lista é um recorte, e permite pedir mais.
   */
  limit?: number;
  enabled?: boolean;
};

/**
 * As poucas tabelas que não têm `org_id`, e por isso não recebem o filtro de
 * organização ativa. Cada uma por um motivo próprio: `organizations` é a
 * própria organização, `profiles` é da pessoa, `bio_entitlements` deriva do
 * site, e as três da Yard atravessam clientes por definição.
 */
const SEM_ORG = new Set<Table>([
  'profiles',
  'organizations',
  'feature_flags',
  'platform_admins',
  'yard_clientes',
  'yard_usuarios',
  'yard_convites',
  'yard_auditoria',
  'yard_recursos',
  'bio_entitlements',
]);

function client() {
  if (!supabase) throw new Error('Supabase não configurado. Verifique o .env.');
  return supabase;
}

/**
 * Lista uma tabela do tenant ativo.
 *
 * O filtro por `org_id` vai na consulta, e não só na RLS. A política do banco
 * libera toda organização em que a pessoa é membro (`auth_org_ids()`), o que
 * está certo para o banco: ela protege contra quem não tem vínculo. Mas quem
 * atende dois clientes tem vínculo com os dois, e via os dois misturados na
 * mesma tela, com a organização ativa servindo só de chave de cache. Em
 * `/russirussi/bio` aparecia a Bio da CIMA junto.
 *
 * Aqui a organização ativa entra na consulta. A RLS continua sendo a defesa
 * contra estranho; esta linha é a separação entre clientes de quem entrou.
 */
export function useList<T extends Row = Row>(table: Table, opts: ListOptions = {}) {
  const { org, status } = useAuth();
  const orgId = org?.id ?? null;

  return useQuery<T[], Error>({
    queryKey: [table, orgId, opts.select ?? '*', opts.orderBy ?? '', opts.eq ?? {}, opts.gte ?? {}, opts.limit ?? 0],
    enabled: status === 'ready' && !!orgId && opts.enabled !== false,
    staleTime: 15_000,
    queryFn: async () => {
      let q = client().from(table).select(opts.select ?? '*');
      if (orgId && !SEM_ORG.has(table)) q = q.eq('org_id', orgId);
      for (const [k, v] of Object.entries(opts.eq ?? {})) {
        q = v === null ? q.is(k, null) : q.eq(k, v);
      }
      for (const [k, v] of Object.entries(opts.gte ?? {})) q = q.gte(k, v);
      if (opts.orderBy) q = q.order(opts.orderBy, { ascending: opts.ascending ?? true });
      if (opts.limit) q = q.limit(opts.limit);
      const { data, error } = await q;
      if (error) throw new Error(traduzir(error.message, error.code));
      return (data ?? []) as unknown as T[];
    },
  });
}

/**
 * Lista sem tenant. Serve só ao console da Yard, cujas telas atravessam
 * organizações e cujo administrador pode não pertencer a nenhuma — nesse caso
 * `useList` nunca dispararia, porque espera um `org_id` ativo. Quem filtra aqui
 * é a RLS: fora da equipe Yard estas consultas voltam vazias.
 */
export function useListGlobal<T extends Row = Row>(table: Table, opts: ListOptions = {}) {
  const { status, user } = useAuth();

  return useQuery<T[], Error>({
    // B7: a chave leva o usuário. Sem tenant para separar quem é quem, era
    // esta chave (e a de `is_platform_admin` em `platform/Console.tsx`) que
    // sobrevivia à troca de sessão sem invalidar - a pessoa nova via, por um
    // instante, o que a consulta da pessoa anterior tinha trazido.
    queryKey: [table, 'global', user?.id ?? null, opts.select ?? '*', opts.orderBy ?? '', opts.eq ?? {}],
    enabled: status === 'ready' && opts.enabled !== false,
    staleTime: 15_000,
    queryFn: async () => {
      let q = client().from(table).select(opts.select ?? '*');
      for (const [k, v] of Object.entries(opts.eq ?? {})) {
        q = v === null ? q.is(k, null) : q.eq(k, v);
      }
      if (opts.orderBy) q = q.order(opts.orderBy, { ascending: opts.ascending ?? true });
      const { data, error } = await q;
      if (error) throw new Error(traduzir(error.message, error.code));
      return (data ?? []) as unknown as T[];
    },
  });
}

/**
 * Busca um registro por id, dentro da organização ativa.
 *
 * P2/P3: a chave de cache já levava `orgId`, mas a consulta em si só fazia
 * `eq('id', id)` - o comentário antigo ("fora do tenant, a RLS devolve
 * vazio") só é verdade para quem não tem vínculo nenhum com a outra
 * organização. Quem atende dois clientes TEM vínculo com os dois
 * (`auth_org_ids()` inclui as duas), e colar o id de um registro do cliente
 * B na URL enquanto o cliente A está ativo devolvia o registro de B mesmo
 * assim - a RLS deixava passar, só a organização ativa (que nunca entrava
 * na consulta) devia ter barrado. Mesmo filtro de `useList`, mesma lista de
 * exceção (`SEM_ORG`): `organizations` é a própria organização, `profiles`
 * é da pessoa - nenhuma das duas tem `org_id` para filtrar.
 */
export function useOne<T extends Row = Row>(table: Table, id: string | undefined, select = '*') {
  const { org, status } = useAuth();
  const orgId = org?.id ?? null;

  return useQuery<T | null, Error>({
    queryKey: [table, orgId, 'one', id, select],
    enabled: status === 'ready' && (!!orgId || SEM_ORG.has(table)) && !!id,
    // L (Bio): sem `staleTime` o padrão é zero, e cada montagem refazia a
    // consulta - a moldura de um site do Bio (`SiteFrame`) monta de novo a
    // cada troca de aba, e cada troca buscava o site outra vez. Mesmo prazo
    // de `useList`; as mutações invalidam pela chave da tabela, então dado
    // recém-salvo continua chegando na hora.
    staleTime: 15_000,
    queryFn: async () => {
      let q = client().from(table).select(select).eq('id', id!);
      if (orgId && !SEM_ORG.has(table)) q = q.eq('org_id', orgId);
      const { data, error } = await q.maybeSingle();
      if (error) throw new Error(traduzir(error.message, error.code));
      return (data as unknown as T) ?? null;
    },
  });
}

function useInvalidate(table: Table, extras: readonly string[] = []) {
  const qc = useQueryClient();
  const { org } = useAuth();
  return () => {
    void qc.invalidateQueries({ queryKey: [table, org?.id ?? null] });
    for (const chave of extras) void qc.invalidateQueries({ queryKey: [chave, org?.id ?? null] });
  };
}

/**
 * O toast de confirmação, para as três mutações abaixo.
 *
 * Fica aqui, num lugar só, porque "criar", "salvar" e "remover" acontecem em
 * centenas de telas — Contatos, Automações, Bio, CRM, o suitemail, todas
 * chamam `useInsert`/`useUpdate`/`useRemove` — e antes disso nenhuma delas
 * dizia nada quando dava certo. `avisar` some sozinho; passe `mensagem: false`
 * para uma mutação que já tem sua própria confirmação na tela (ex.: um botão
 * que já vira "Salvo" por 1,5s) e não precisa da segunda voz dizendo o mesmo.
 */
type OpcoesDeAviso = {
  mensagem?: string | false;
  /**
   * Outras chaves de cache que a escrita deixa velhas, além da própria
   * tabela. O painel e o relatório do CRM contam negócios por RPC
   * (`crm_painel`, `crm_relatorio_estrategico`), com chave própria: sem
   * isto, mover um negócio deixava o painel com o número antigo até o
   * `staleTime` vencer.
   */
  invalidar?: readonly string[];
};

export function useInsert<T extends Record<string, unknown>>(table: Table, opcoes?: OpcoesDeAviso) {
  const invalidate = useInvalidate(table, opcoes?.invalidar);
  const avisar = useAvisar();
  return useMutation<Row, Error, T>({
    mutationFn: async (values) => {
      // Sem org_id: quem carimba é o trigger, a partir do JWT.
      const { data, error } = await client()
        .from(table)
        .insert(values as Record<string, unknown>)
        .select()
        .single();
      if (error) throw new Error(traduzir(error.message, error.code));
      return data as Row;
    },
    onSuccess: () => {
      invalidate();
      if (opcoes?.mensagem !== false) avisar({ tom: 'ok', texto: opcoes?.mensagem ?? 'Criado.' });
    },
  });
}

export function useUpdate<T extends Record<string, unknown>>(table: Table, opcoes?: OpcoesDeAviso) {
  const invalidate = useInvalidate(table, opcoes?.invalidar);
  const avisar = useAvisar();
  return useMutation<Row, Error, { id: string; values: T }>({
    mutationFn: async ({ id, values }) => {
      const { data, error } = await client()
        .from(table)
        .update(values as Record<string, unknown>)
        .eq('id', id)
        .select()
        .single();
      if (error) throw new Error(traduzir(error.message, error.code));
      return data as Row;
    },
    onSuccess: () => {
      invalidate();
      if (opcoes?.mensagem !== false) avisar({ tom: 'ok', texto: opcoes?.mensagem ?? 'Salvo.' });
    },
  });
}

export function useRemove(table: Table, opcoes?: OpcoesDeAviso) {
  const invalidate = useInvalidate(table, opcoes?.invalidar);
  const avisar = useAvisar();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const { error } = await client().from(table).delete().eq('id', id);
      if (error) throw new Error(traduzir(error.message, error.code));
    },
    onSuccess: () => {
      invalidate();
      if (opcoes?.mensagem !== false) avisar({ tom: 'ok', texto: opcoes?.mensagem ?? 'Removido.' });
    },
  });
}

/**
 * Distingue "a tabela ainda não existe" de "deu erro".
 *
 * São coisas diferentes na tela: falta de migração é uma superfície que ainda
 * não liga - a tela mostra o que ela VAI ser, desligada e dizendo por quê. Erro
 * de verdade é uma faixa vermelha com botão de tentar de novo. Tratar as duas
 * igual faz o time achar que o produto está quebrado quando ele só está
 * esperando um `supabase db push`.
 */
export function aguardandoMigracao(erro: unknown): boolean {
  const m = erro instanceof Error ? erro.message : '';
  return m.includes('ainda não existe') || m.includes('não aplicada');
}

/**
 * Mensagens do Postgres viram texto que o usuário entende.
 *
 * Exportada para quem monta a própria consulta fora de `useList`/`useOne`
 * (L4 - Triagem.tsx precisa filtrar `portal_mensagens` no servidor, algo que
 * o formato de `eq` destes hooks não expressa) e ainda assim precisa do
 * mesmo texto - inclusive o que `aguardandoMigracao` procura, que é a
 * mensagem TRADUZIDA ("...ainda não aplicada"), não a bruta do Postgres.
 */
export function traduzir(message: string, code?: string): string {
  if (code === '42501' || message.includes('row-level security')) {
    return 'Você não tem permissão para esta operação nesta organização.';
  }
  // Índice único da 0109: um estágio de ganho e um de perdido por funil.
  if (code === '23505' && /stages_um_(won|lost)_por_funil/.test(message)) {
    return 'Este funil já tem uma etapa desse tipo de fechamento. Cada funil tem exatamente uma de ganho e uma de perdido.';
  }
  if (code === '23505') return 'Já existe um registro com esse valor.';
  // Update que a RLS filtrou (0109: STAFF só edita o negócio que é seu) não
  // devolve erro de permissão, devolve zero linhas - e o `.single()` acusa.
  if (code === 'PGRST116') {
    return 'Você não tem permissão para alterar este registro, ou ele não existe mais.';
  }
  if (code === '23503') return 'Este registro depende de outro que não existe mais.';
  if (code === 'PGRST205' && /companies|crm_settings/.test(message)) {
    return 'Empresas e a configuração da equipe dependem da migração 0109_crm_permissoes_empresas_e_automacoes.sql, ainda não aplicada.';
  }
  if (code === 'PGRST202' && /crm_criar_funil/.test(message)) {
    return 'Criar funil depende da migração 0109_crm_permissoes_empresas_e_automacoes.sql, ainda não aplicada.';
  }
  if (code === 'PGRST205') {
    if (/portal_(pastas|documento_compartilhamentos|documento_pessoas)/.test(message)) {
      return 'Pastas e níveis de compartilhamento dependem da migração 0105_reaplica_pastas_selos_e_triagem.sql, ainda não aplicada.';
    }
    if (/reuniao_(transcricao|ata|gravacao)/.test(message)) {
      return 'Ata, transcrição e gravação da reunião dependem da migração 0069_ata_e_gravacao_da_reuniao.sql, ainda não aplicada.';
    }
    if (message.includes('portal_delegacoes')) {
      return 'Delegar um cartão depende da migração 0070_delegacoes_da_triagem.sql, ainda não aplicada.';
    }
    if (message.includes('portal_')) {
      return 'As tabelas do Portal ainda não existem no banco. Rode a migração 0062_portal_mail.sql.';
    }
    return message.includes('bio_')
      ? 'A tabela do Bio ainda não existe no banco. Rode a migração 0012_bio.sql.'
      : 'A tabela ainda não existe no banco. Rode a migração 0003_foundation.sql.';
  }
  // PGRST202: a função (RPC) não existe. As do painel do Marketing nascem na
  // 0096; até ela rodar, a tela diz de que migração depende, em vez de
  // mostrar o nome interno da função.
  if (code === 'PGRST202' && /mkt_painel|mkt_automacoes_metricas/.test(message)) {
    return 'Os números do painel do Marketing dependem da migração 0096_painel_do_marketing_conta_no_banco.sql, ainda não aplicada.';
  }
  // Os relatórios estratégicos (CRM, Marketing, Flow) nascem na 0098.
  if (code === 'PGRST202' && /(crm|mkt|flw)_relatorio_estrategico/.test(message)) {
    return 'O relatório estratégico depende da migração 0098_relatorios_crm_mkt_flow.sql, ainda não aplicada.';
  }
  if (message.includes('handle_reservado')) {
    return 'Este endereço é reservado pelo sistema. Escolha outro.';
  }
  if (message.includes('handle_invalido')) {
    return 'Use de 2 a 32 caracteres, apenas letras minúsculas, números e hífen.';
  }
  if (message.includes('handle_indisponivel')) {
    return 'Este endereço já está em uso. Escolha outro.';
  }
  return message;
}

/**
 * Chamada a uma função do banco (RPC).
 *
 * Usada onde a regra precisa rodar no servidor: peso de quiz, validação de
 * cupom, publicação atômica, leitura pública por handle. O cliente nunca
 * recebe o dado bruto dessas tabelas.
 */
export async function chamarRpc<T>(nome: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await client().rpc(nome, args);
  if (error) throw new Error(traduzir(error.message, error.code));
  return data as T;
}

/** Mesma chamada, sem exigir sessão: serve às páginas públicas. */
export async function chamarRpcPublico<T>(
  nome: string,
  args: Record<string, unknown> = {},
): Promise<T> {
  if (!supabase) throw new Error('Supabase não configurado. Verifique o .env.');
  const { data, error } = await supabase.rpc(nome, args);
  if (error) throw new Error(traduzir(error.message, error.code));
  return data as T;
}
